/* Livways storefront: gallery position, compact offer launchers, footer Inbox entry. */
(() => {
  'use strict';
  if (window.LivwaysStorefrontUX) return;

  // Native grid galleries do not update .is-active after every mobile swipe.
  // A slide event owns the position until the native active identity changes.
  function position(ids, active, previousActive, current) {
    const unique = [...new Set(ids)];
    const id = active !== previousActive && unique.includes(active) ? active
      : unique.includes(current) ? current : unique.includes(active) ? active : unique[0];
    return {id, index: unique.indexOf(id), total: unique.length, active};
  }

  class GalleryCounter {
    constructor(gallery) {
      this.gallery = gallery;
      this.viewer = gallery.querySelector('[id^="GalleryViewer"]');
      if (!this.viewer) return;
      this.nav = document.createElement('div');
      this.nav.className = 'lvs-gallery-counter';
      this.nav.setAttribute('role', 'group');
      this.nav.setAttribute('aria-label', 'Product images');
      this.nav.innerHTML = '<button type="button" aria-label="Previous image"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="m14 7-5 5 5 5"/></svg></button><span class="lvs-gallery-position" aria-live="polite" aria-atomic="true"></span><button type="button" aria-label="Next image"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="m10 7 5 5-5 5"/></svg></button>';
      [this.prev, this.next] = this.nav.querySelectorAll('button');
      this.label = this.nav.querySelector('span');
      this.prev.addEventListener('click', () => this.move(-1));
      this.next.addEventListener('click', () => this.move(1));
      this.viewer.after(this.nav);
      this.onSlide = event => {
        const item = event.detail?.currentElement;
        if (!item || item.hasAttribute('data-erp-color-hidden') || item.classList.contains('hidden')) return;
        this.current = item.dataset.mediaId;
        this.previousActive = this.active();
        this.refresh();
      };
      this.viewer.addEventListener('slideChanged', this.onSlide);
      this.observer = new MutationObserver(() => this.schedule());
      this.observer.observe(this.viewer, {subtree: true, childList: true, attributes: true,
        attributeFilter: ['class', 'style', 'hidden', 'data-erp-color-hidden']});
      this.refresh();
      gallery.classList.add('lvs-counter-ready');
    }
    active() { return this.viewer.querySelector('.product__media-item.is-active')?.dataset.mediaId; }
    slides() {
      return [...this.viewer.querySelectorAll('.product__media-item[data-media-id]')].filter(item =>
        !item.hidden && !item.hasAttribute('data-erp-color-hidden') && !item.classList.contains('hidden') &&
        getComputedStyle(item).display !== 'none');
    }
    schedule() {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => { this.frame = 0; this.refresh(); });
    }
    refresh() {
      const state = position(this.slides().map(item => item.dataset.mediaId), this.active(), this.previousActive, this.current);
      this.current = state.id;
      this.previousActive = state.active;
      this.state = state;
      const text = state.total ? `${state.index + 1} / ${state.total}` : '';
      if (this.label.textContent !== text) this.label.textContent = text;
      this.nav.hidden = !state.total;
      this.prev.disabled = state.index <= 0;
      this.next.disabled = state.index >= state.total - 1;
    }
    move(direction) {
      this.refresh();
      const ids = [...new Set(this.slides().map(item => item.dataset.mediaId))];
      const id = ids[this.state.index + direction];
      if (!id) return;
      this.current = id;
      if (typeof this.gallery.setActiveMedia === 'function') this.gallery.setActiveMedia(id, false);
      else this.slides().find(item => item.dataset.mediaId === id)?.scrollIntoView({block: 'nearest', inline: 'start'});
      this.refresh();
    }
    destroy() {
      this.observer?.disconnect();
      cancelAnimationFrame(this.frame);
      this.viewer.removeEventListener('slideChanged', this.onSlide);
      this.nav.remove();
      this.gallery.classList.remove('lvs-counter-ready');
    }
  }

  const mail = '<span class="lvs-offer-mail" aria-hidden="true"><svg viewBox="0 0 24 24"><rect x="2" y="5" width="20" height="14" rx="2.5"/><path d="m3 6 7.6 5.8a2.3 2.3 0 0 0 2.8 0L21 6"/></svg><span class="lvs-offer-dot"></span></span>';
  const ticket = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 5h16v5a2 2 0 0 0 0 4v5H4v-5a2 2 0 0 0 0-4V5Zm11 0v3m0 3v2m0 3v3"/></svg>';
  const offerDefinitions = [
    {key: 'referral', selector: '#loox-visitor-level-referral-btn', root: '.loox-float-toggler-container', label: 'Get $10', icon: ticket},
    {key: 'newsletter', selector: '.kl-teaser-Y6LV4B [role="button"]', root: '.kl-teaser-Y6LV4B', label: 'Special offers', icon: mail}
  ];
  const offerTiming = {idle: 1200, expandGap: 800, collapseGap: 220};

  class Offers {
    constructor() {
      this.entries = new Map();
      this.motionTimers = [];
      this.closed = new Set();
      try { for (const def of offerDefinitions) if (sessionStorage.getItem(`lvs-offer-dismissed:${def.key}`) === '1') this.closed.add(def.key); } catch (_) { /* Session-only fallback when storage is blocked. */ }
      this.dock = document.createElement('aside');
      this.dock.className = 'lvs-offer-dock';
      this.dock.setAttribute('aria-label', 'Offers');
      document.body.append(this.dock);
      this.scroll = () => {
        this.scrolling = true;
        this.setExpanded(false);
        this.idleSoon();
        this.checkOverlays();
      };
      this.dock.addEventListener('pointerenter', event => {
        if (event.pointerType !== 'touch' && !this.scrolling) this.setExpanded(true);
      });
      this.dock.addEventListener('focusin', event => {
        if (event.target.matches(':focus-visible')) this.setExpanded(true);
      });
      window.addEventListener('scroll', this.scroll, {passive: true});
      document.addEventListener('focusin', () => this.checkOverlays());
      document.addEventListener('focusout', () => requestAnimationFrame(() => this.checkOverlays()));
      window.visualViewport?.addEventListener('resize', () => this.checkOverlays());
      this.idleSoon();
    }
    setExpanded(expanded, immediate = false) {
      // Repeated scroll events must not postpone the second pill's collapse.
      if (this.targetExpanded === expanded && !immediate) return;
      this.targetExpanded = expanded;
      for (const timer of this.motionTimers) clearTimeout(timer);
      this.motionTimers = [];
      if (expanded) this.dock.classList.add('is-idle'); else this.dock.classList.remove('is-idle');
      const entries = offerDefinitions.map(def => this.entries.get(def.key)).filter(entry => entry && !entry.row.hidden);
      const apply = entry => {
        if (expanded) entry.row.classList.add('is-expanded'); else entry.row.classList.remove('is-expanded');
        entry.row.querySelector('.lvs-offer-close').disabled = !expanded;
      };
      entries.forEach((entry, index) => {
        if (immediate || index === 0) apply(entry);
        else this.motionTimers.push(setTimeout(() => apply(entry), index * (expanded ? offerTiming.expandGap : offerTiming.collapseGap)));
      });
    }
    idleSoon() {
      clearTimeout(this.timer);
      this.timer = setTimeout(() => { this.scrolling = false; this.setExpanded(true); }, offerTiming.idle);
    }
    mount() {
      let addedEntry = false;
      for (const def of offerDefinitions) {
        const original = document.querySelector(def.selector);
        const root = original?.closest(def.root);
        const old = this.entries.get(def.key);
        if (!original || !root) { if (old) old.row.hidden = true; continue; }
        let entry = old;
        if (!entry) {
          const row = document.createElement('div');
          row.className = `lvs-offer lvs-offer--${def.key}`;
          row.innerHTML = `<button type="button" class="lvs-offer-open" aria-label="${def.label}">${def.icon}<span class="lvs-offer-label">${def.label}</span></button><button type="button" class="lvs-offer-close" aria-label="Dismiss ${def.label}"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="m6 6 12 12M18 6 6 18"/></svg></button>`;
          entry = {row};
          row.querySelector('.lvs-offer-close').disabled = true;
          const button = row.querySelector('.lvs-offer-open');
          button.addEventListener('click', () => {
            // Use the actual app's existing handler, targeting and campaign; no new subscriptions.
            entry.original?.click();
            clearTimeout(this.timer);
            this.setExpanded(false);
            setTimeout(() => this.checkOverlays(), 150);
          });
          row.querySelector('.lvs-offer-close').addEventListener('click', event => {
            this.closed.add(def.key);
            try { sessionStorage.setItem(`lvs-offer-dismissed:${def.key}`, '1'); } catch (_) {}
            row.hidden = true;
            const next = [...this.entries.values()].find(other => !other.row.hidden);
            if (event.detail === 0) next?.row.querySelector('.lvs-offer-open').focus({preventScroll: true});
          });
          this.dock.append(row);
          this.entries.set(def.key, entry);
          addedEntry = true;
        }
        if (entry.root !== root) {
          entry.observer?.disconnect();
          entry.root?.classList.remove('lvs-original-launcher');
          entry.root = root;
          entry.observer = new MutationObserver(() => {
            entry.row.hidden = this.closed.has(def.key) || getComputedStyle(root).display === 'none' || root.hidden;
          });
          entry.observer.observe(root, {attributes: true, attributeFilter: ['style', 'hidden', 'class']});
        }
        entry.original = original;
        entry.row.hidden = this.closed.has(def.key) || getComputedStyle(root).display === 'none' || root.hidden;
        root.classList.add('lvs-original-launcher');
        root.setAttribute('aria-hidden', 'true');
        root.setAttribute('inert', '');
      }
      if (addedEntry) {
        // Stable visual and keyboard order even when the two apps load in reverse order.
        for (const def of offerDefinitions) { const entry = this.entries.get(def.key); if (entry) this.dock.append(entry.row); }
        this.setExpanded(false, true);
        this.idleSoon();
      }
      this.checkOverlays();
    }
    checkOverlays() {
      const active = document.activeElement;
      const typing = /^(TEXTAREA|SELECT)$/.test(active?.tagName) || active?.isContentEditable ||
        active?.tagName === 'INPUT' && /^(text|email|search|tel|url|password|number)$/.test(active.type || 'text');
      const selectors = 'dialog[open], [aria-modal="true"], cart-drawer.active, .menu-drawer-container[open], shopify-chat[open]';
      const overlay = [...document.querySelectorAll(selectors)].some(node => {
        if (node.closest('cart-drawer:not(.active),product-modal:not([open]),modal-dialog:not([open]),details:not([open])')) return false;
        const rect = node.getBoundingClientRect(), style = getComputedStyle(node);
        return rect.width > 0 && rect.height > 0 && rect.right > 0 && rect.bottom > 0 &&
          rect.left < window.innerWidth && rect.top < window.innerHeight && style.visibility !== 'hidden' && style.opacity !== '0';
      });
      const chat = document.querySelector('shopify-chat');
      const keyboard = window.visualViewport && window.visualViewport.height < window.innerHeight * .75;
      this.dock.hidden = !!(typing || overlay || chat?.open || keyboard);
    }
  }

  window.LivwaysStorefrontUX = {position, GalleryCounter, Offers};
  if (typeof document === 'undefined') return;
  const counters = new Map();
  let offers, scheduled = false;
  function mount() {
    scheduled = false;
    for (const [gallery, controller] of counters) if (!gallery.isConnected) { controller.destroy(); counters.delete(gallery); }
    document.querySelectorAll('media-gallery').forEach(gallery => {
      if (!counters.has(gallery) && gallery.querySelector('[id^="GalleryViewer"]')) counters.set(gallery, new GalleryCounter(gallery));
    });
    offers?.mount();
    const chat = document.querySelector('shopify-chat');
    if (chat && typeof chat.show === 'function' && document.querySelector('[data-lvs-online-chat]')) document.documentElement.classList.add('lvs-footer-chat-ready');
  }
  function schedule() {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(mount);
  }
  function start() {
    offers = new Offers();
    mount();
    // Child-list only: avoid reacting to app animations or our own scroll classes.
    new MutationObserver(records => {
      if (records.some(record => [...record.addedNodes, ...record.removedNodes].some(node => node.nodeType === 1 && !node.closest?.('.lvs-offer-dock, .lvs-gallery-counter')))) schedule();
    }).observe(document.body, {subtree: true, childList: true});
    new MutationObserver(records => {
      if (records.some(record => record.target.matches('body,dialog,[aria-modal],cart-drawer,details,shopify-chat'))) offers.checkOverlays();
    }).observe(document.body, {subtree: true, attributes: true, attributeFilter: ['class', 'style', 'open', 'aria-hidden']});
    document.addEventListener('shopify:section:load', schedule);
    customElements.whenDefined('media-gallery').then(schedule);
    customElements.whenDefined('shopify-chat').then(schedule);
    document.addEventListener('click', event => {
      const link = event.target.closest('[data-lvs-online-chat]');
      const chat = document.querySelector('shopify-chat');
      if (!link || !chat || typeof chat.show !== 'function' || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      chat.show();
      offers.checkOverlays();
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, {once: true}); else start();
})();
